import { addLocation } from "@redotech/redo-merchant-fulfillment-rpc/schema/add-location";
import { deleteLocation } from "@redotech/redo-merchant-fulfillment-rpc/schema/delete-location";
import { getLocations } from "@redotech/redo-merchant-fulfillment-rpc/schema/get-locations";
import { getLocationsWithFulfillsFor } from "@redotech/redo-merchant-fulfillment-rpc/schema/get-locations-with-fulfills-for";
import { updateLocation } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-location";
import { getExchangeRates } from "@redotech/redo-model/money/get-exchange-rates";
import { getWarrantyEligibleProductsSchema as getWarrantyEligibleProducts } from "@redotech/redo-model/warranties/get-warranty-eligible-products-schema";
import { submitWarrantyRegistrationSchema as submitWarrantyRegistration } from "@redotech/redo-model/warranties/submit-warranty-registration-schema";
import type { InferRpcDefinition } from "@redotech/rpc/definition";
import {
  disconnectAims360Integration,
  getAims360Integration,
  setAims360Integration,
  updateAims360Integration,
} from "../returns-rpc/schema/aims360";
import {
  disconnectApparelMagicIntegration,
  getApparelMagicIntegration,
  setApparelMagicIntegration,
  updateApparelMagicIntegration,
} from "../returns-rpc/schema/apparel-magic";
import {
  disconnectAuthorizeNetIntegration,
  getAuthorizeNetIntegration,
  setAuthorizeNetIntegration,
  updateAuthorizeNetIntegration,
} from "../returns-rpc/schema/authorizenet";
import {
  disconnectBlueYonderIntegration,
  getBlueYonderIntegration,
  setBlueYonderIntegration,
} from "../returns-rpc/schema/blue-yonder";
import { createRmaForReturn } from "../returns-rpc/schema/create-rma-for-return";
import { disconnectCreditsYardIntegration } from "../returns-rpc/schema/creditsyard/disconnect-creditsyard-integration";
import { getCreditsYardIntegration } from "../returns-rpc/schema/creditsyard/get-creditsyard-integration";
import { setCreditsYardIntegration } from "../returns-rpc/schema/creditsyard/set-creditsyard-integration";
import { updateCreditsYardIntegration } from "../returns-rpc/schema/creditsyard/update-creditsyard-integration";
import {
  disconnectCustomReturnIntegration,
  getCustomReturnIntegration,
  setCustomReturnIntegration,
} from "../returns-rpc/schema/custom-return-integration";
import {
  disconnectFlexportIntegration,
  getFlexportIntegration,
  putFlexportIntegration,
} from "../returns-rpc/schema/flexport";
import { getValidReturnTrackingTriggers } from "../returns-rpc/schema/get-valid-return-tracking-triggers";
import { getWorkflowStatus } from "../returns-rpc/schema/get-workflow-status";
import {
  disconnectInmarIntegration,
  getInmarIntegration,
  setInmarIntegration,
} from "../returns-rpc/schema/inmar";
import {
  disconnectKlaviyoIntegration,
  getKlaviyoIntegration,
  setKlaviyoIntegration,
  updateKlaviyoIntegration,
} from "../returns-rpc/schema/klaviyo";
import {
  disconnectLoopIntegration,
  getLoopIntegration,
  setLoopIntegration,
  updateLoopIntegration,
} from "../returns-rpc/schema/loop";
import {
  disconnectPurpleDotIntegration,
  getPurpleDotIntegration,
  setPurpleDotIntegration,
  updatePurpleDotIntegration,
} from "../returns-rpc/schema/purple-dot";
import {
  disconnectReturnBearIntegration,
  getReturnBearIntegration,
  setReturnBearIntegration,
  updateReturnBearIntegration,
} from "../returns-rpc/schema/returnbear";
import {
  disconnectRiseAiIntegration,
  getRiseAiIntegration,
  setRiseAiIntegration,
  updateRiseAiIntegration,
} from "../returns-rpc/schema/rise-ai";
import {
  disconnectSekoIntegration,
  getSekoIntegration,
  setSekoIntegration,
  updateSekoIntegration,
} from "../returns-rpc/schema/seko";
import { sendEmailFromTemplate } from "../returns-rpc/schema/send-email-from-template";
import {
  disconnectShipedgeIntegration,
  getShipedgeIntegration,
  putShipedgeIntegration,
} from "../returns-rpc/schema/shipedge";
import {
  disconnectShipHeroIntegration,
  getShipHeroIntegration,
  putShipheroIntegration,
  shipHeroInventoryAdd,
  validateShipHeroLocation,
} from "../returns-rpc/schema/shiphero";
import { triggerEmailFlowPreview } from "../returns-rpc/schema/trigger-email-flow-preview";
import { getAddressFromSuggestion } from "./schema/address-autofill/get-address-from-suggestion";
import { getAutocompleteSuggestions } from "./schema/address-autofill/get-autocomplete-suggestions";
import { createAddress } from "./schema/addresses/create-address";
import { deleteAddress } from "./schema/addresses/delete-address";
import { getAddressCollections } from "./schema/addresses/get-address-collections";
import { getAddresses } from "./schema/addresses/get-addresses";
import { getAddressesByIds } from "./schema/addresses/get-addresses-by-ids";
import { getOrCreateExternalAddress } from "./schema/addresses/get-or-create-external-address";
import { updateAddress } from "./schema/addresses/update-address";
import { batchUpdateRuleOrder } from "./schema/advanced-flows/batch-update-rule-order";
import { createAdvancedFlow } from "./schema/advanced-flows/create-advanced-flow";
import { createAdvancedFlowFromDefault } from "./schema/advanced-flows/create-advanced-flow-from-default";
import { deleteAdvancedFlow } from "./schema/advanced-flows/delete-advanced-flow";
import { getAdvancedFlowById } from "./schema/advanced-flows/get-advanced-flow-by-id";
import { getAdvancedFlowCount } from "./schema/advanced-flows/get-advanced-flow-count";
import { getAdvancedFlows } from "./schema/advanced-flows/get-advanced-flows";
import { getAdvancedFlowsByVersionGroupId } from "./schema/advanced-flows/get-advanced-flows-by-id";
import { getRulesByTrigger } from "./schema/advanced-flows/get-rules-by-trigger";
import { updateAdvancedFlow } from "./schema/advanced-flows/update-advanced-flow";
import { addAeoPrompts } from "./schema/aeo/add-aeo-prompts";
import { archiveAeoPrompts } from "./schema/aeo/archive-aeo-prompts";
import { createCompetitorOverride } from "./schema/aeo/create-competitor-override";
import { deleteCompetitorOverride } from "./schema/aeo/delete-competitor-override";
import { generateAeoPrompts } from "./schema/aeo/generate-aeo-prompts";
import { getAeoPromptDetails } from "./schema/aeo/get-aeo-prompt-details";
import { getAeoPromptResponses } from "./schema/aeo/get-aeo-prompt-responses";
import { getAeoPrompts } from "./schema/aeo/get-aeo-prompts";
import { getAeoPromptsFiltered } from "./schema/aeo/get-aeo-prompts-filtered";
import { getBrandIdentityGuide } from "./schema/aeo/get-brand-identity-guide";
import { getBrandVisibilityOverTime } from "./schema/aeo/get-brand-visibility-over-time";
import { getCompetitorComparison } from "./schema/aeo/get-competitor-comparison";
import { getCompetitorOverrides } from "./schema/aeo/get-competitor-overrides";
import { getCurrentAeoInvoice } from "./schema/aeo/get-current-aeo-invoice";
import { getDashboardMetrics } from "./schema/aeo/get-dashboard-metrics";
import { getFinalizedAeoInvoices } from "./schema/aeo/get-finalized-aeo-invoices";
import { getModelPerformance } from "./schema/aeo/get-model-performance";
import { getPromptsByCategory } from "./schema/aeo/get-prompts-by-category";
import { getSources } from "./schema/aeo/get-sources";
import { getSourcesByDomain } from "./schema/aeo/get-sources-by-domain";
import { unarchiveAeoPrompts } from "./schema/aeo/unarchive-aeo-prompts";
import { messageCustomerPortalBuilder } from "./schema/agents/customer-portal-builder/message-customer-portal-builder";
import { saveCustomerPortalBuilderChanges } from "./schema/agents/customer-portal-builder/save-customer-portal-builder-changes";
import { queryDom } from "./schema/agents/dom-agent/query-dom";
import {
  createEscalation,
  getEscalation,
  getEscalationMessages,
  sendEscalationMessage,
  watchEscalationEvents,
} from "./schema/agents/merchant-support-agent/escalation";
import {
  getMerchantSupportAgentThread,
  getMerchantSupportAgentThreads,
  messageMerchantSupportAgent,
} from "./schema/agents/merchant-support-agent/message-merchant-support-agent";
import { streamMerchantSupportAgent } from "./schema/agents/merchant-support-agent/stream-merchant-support-agent";
import { provideAiFeedback } from "./schema/agents/provide-ai-feedback";
import { redoAi } from "./schema/agents/redo-ai";
import { generateAiAnalyticsSummary } from "./schema/ai-summary/generate-ai-analytics-summary";
import { getAiAnalyticsSummary } from "./schema/ai-summary/get-ai-analytics-summary";
import { getAiToneAndVoiceSettings } from "./schema/ai-tone-and-voice/get-ai-tone-and-voice-settings";
import { initializeAiToneAndVoice } from "./schema/ai-tone-and-voice/initialize-ai-tone-and-voice";
import { updateAiToneAndVoiceSettings } from "./schema/ai-tone-and-voice/update-ai-tone-and-voice-settings";
import { getAiExchangeAgentAnalytics } from "./schema/analytics/get-ai-exchange-agent-analytics";
import { getClaimAttachmentRateSeriesData } from "./schema/analytics/get-claim-attachment-rate-series-data";
import { getClaimsAnalyticsStatistics } from "./schema/analytics/get-claims-analytics-statistics";
import { getOperationsNewTotals } from "./schema/analytics/get-operations-new-totals";
import { getOrderSourceNames } from "./schema/analytics/get-order-source-names";
import { getPortalEvents } from "./schema/analytics/get-portal-events";
import { getPortalOptimizationState } from "./schema/analytics/get-portal-optimization-state";
import { getProductBenchmark } from "./schema/analytics/get-product-benchmark";
import { getProductTitles } from "./schema/analytics/get-product-titles";
import { getReturnAnalyticsByProduct } from "./schema/analytics/get-return-analytics-by-product";
import { getReturnAnalyticsByVariant } from "./schema/analytics/get-return-analytics-by-variant";
import { getReturnAnalyticsSeriesData } from "./schema/analytics/get-return-analytics-series-data";
import { getReturnAnalyticsStatistics } from "./schema/analytics/get-return-analytics-statistics";
import { getReturnOperationsKeyStats } from "./schema/analytics/get-return-operations-key-stats";
import { getTags } from "./schema/analytics/get-tags";
import { getTryFirstAnalytics } from "./schema/analytics/get-try-first-analytics";
import { getVendorNames } from "./schema/analytics/get-vendor-names";
import {
  createReport,
  deleteReport,
  getReport,
  getReports,
  streamOrdersReportData,
  streamProductsReportData,
  streamPurchasedExtendedWarrantyReportData,
  streamWarrantyCustomersReportData,
  updateReport,
} from "./schema/analytics/reports";
import { addAssessmentToReturn } from "./schema/assessments/add-assessment-to-return";
import { completeAssessmentOnReturn } from "./schema/assessments/complete-assessment-on-return";
import { createAssessment } from "./schema/assessments/create-assessment";
import { createGroup } from "./schema/assessments/create-group";
import { deleteAssessment } from "./schema/assessments/delete-assessment";
import { deleteGroup } from "./schema/assessments/delete-group";
import { getAssessmentDefinition } from "./schema/assessments/get-assessment-definition";
import { getAssessmentGroupList } from "./schema/assessments/get-assessment-group-list";
import { getAssessmentList } from "./schema/assessments/get-assessment-list";
import { getReturnProductAssessments } from "./schema/assessments/get-return-product-assessments";
import { removeAssessmentFromReturn } from "./schema/assessments/remove-assessment-from-return";
import { updateAssessment } from "./schema/assessments/update-assessment";
import { updateGroup } from "./schema/assessments/update-group";
import { updateProductAssessmentAssignee } from "./schema/assessments/update-product-assessment-assignee";
import { createStripeConnectedAccount } from "./schema/billing/create-stripe-connected-account";
import { getStripeConnectedAccount } from "./schema/billing/get-stripe-connected-account";
import { onboardStripeConnectedAccount } from "./schema/billing/onboard-stripe-connected-account";
import { triggerManualPayout } from "./schema/billing/trigger-manual-payout";
import { generateContactCard } from "./schema/brand-kit/generate-contact-card";
import { processFontFiles } from "./schema/brand-kit/process-font-files";
import { updateBrandKit } from "./schema/brand-kit/update-brand-kit";
import { calculateNewOrderValue } from "./schema/calculate-new-order-value";
import { cancelOrder } from "./schema/cancel-order";
import { authorizeUspsCarrierAccount } from "./schema/carrier-accounts/authorize-usps-carrier-account";
import { createCarrierAccount } from "./schema/carrier-accounts/create-carrier-account";
import { createDirectFedexCarrierAccount } from "./schema/carrier-accounts/create-direct-fedex-carrier-account";
import { deleteCarrierAccount } from "./schema/carrier-accounts/delete-carrier-account";
import { generateFedexPin } from "./schema/carrier-accounts/generate-fedex-pin";
import { getCarrierAccounts } from "./schema/carrier-accounts/get-carrier-accounts";
import { submitFedexMfaBackup } from "./schema/carrier-accounts/submit-fedex-mfa-backup";
import { submitFedexMfaData } from "./schema/carrier-accounts/submit-fedex-mfa-data";
import { verifyUspsContractRatesLinked } from "./schema/carrier-accounts/verify-usps-contract-rates-linked";
import { editCartOptimizationSettings } from "./schema/cart-optimization/edit-cart-optimization-settings";
import { checkOrdersReturnability } from "./schema/check-orders-returnability";
import { fastTrackClaim } from "./schema/claims/fast-track-claim";
import { getCollections } from "./schema/collections/get-collections";
import { createOrderComment } from "./schema/create-order-comment";
import { disconnectRivoIntegration } from "./schema/customer-accounts/rivo/disconnect-rivo-integration";
import { getRivoIntegration } from "./schema/customer-accounts/rivo/get-rivo-integration";
import { setRivoIntegration } from "./schema/customer-accounts/rivo/set-rivo-integration";
import { getNativeAccountsSettings } from "./schema/customer-accounts/storefront-display-settings/get-native-accounts-settings";
import { updateNativeAccountsSettings } from "./schema/customer-accounts/storefront-display-settings/update-native-accounts-settings";
import { updateAllCustomerWidgetSettings } from "./schema/customer-widget/update-all-customer-widget-settings";
import { updateCustomerWidgetSettings } from "./schema/customer-widget/update-customer-widget-settings";
import { adjustLoyaltyPoints } from "./schema/customer/adjust-loyalty-points";
import { createCustomerAuthToken } from "./schema/customer/create-customer-auth-token";
import { getCustomer } from "./schema/customer/get-customer";
import { getCustomerByEmail } from "./schema/customer/get-customer-by-email";
import { getCustomerTags } from "./schema/customer/get-customer-tags";
import { getOrCreateCustomerFromPhoneNumber } from "./schema/customer/get-or-create-customer-from-phone-number";
import { generateCustomerCommunicationEvidence } from "./schema/disputes/generate-customer-communication-evidence";
import { generateDisputeActivityLog } from "./schema/disputes/generate-dispute-activity-log";
import { getDispute } from "./schema/disputes/get-dispute";
import { getDisputes } from "./schema/disputes/get-disputes";
import { getShopPolicies } from "./schema/disputes/get-shop-policies";
import { submitDisputeEvidence } from "./schema/disputes/submit-dispute-evidence";
import { updateDisputeEvidence } from "./schema/disputes/update-dispute-evidence";
import { updateShopPolicy } from "./schema/disputes/update-shop-policy";
import { createEmailTemplateFromDefault } from "./schema/email-templates/create-email-template-from-default";
import { createArticle } from "./schema/faq/create-article";
import { createCollection } from "./schema/faq/create-collection";
import { createFaqPage } from "./schema/faq/create-faq-page";
import { deleteArticle } from "./schema/faq/delete-article";
import { deleteCollection } from "./schema/faq/delete-collection";
import { deleteFaqPage } from "./schema/faq/delete-faq-page";
import { getFaq } from "./schema/faq/get-faq";
import { getFaqArticles } from "./schema/faq/get-faq-articles";
import { getFaqCollections } from "./schema/faq/get-faq-collections";
import { getFaqPages } from "./schema/faq/get-faq-pages";
import { getFaqTheme } from "./schema/faq/get-faq-theme";
import { updateArticle } from "./schema/faq/update-article";
import { updateCollection } from "./schema/faq/update-collection";
import { updateFaqPage } from "./schema/faq/update-faq-page";
import { updateTheme } from "./schema/faq/update-theme";
import { findPreviousReturnShippingRefunds } from "./schema/find-previous-return-shipping-refunds";
import { generateFreightQuotes } from "./schema/freight/generate-freight-quotes";
import { purchaseFreightBooking } from "./schema/freight/purchase-freight-booking";
import { generateFlowAgentResponse } from "./schema/generate-flow-agent-response";
import { getBatchTnplContractStatus } from "./schema/get-batch-tnpl-contract-status";
import { getBundlesData } from "./schema/get-bundles-data";
import { getCollectionInfoByIds } from "./schema/get-collections";
import { getCustomerAccountsMetricsForOverview } from "./schema/get-customer-accounts-metrics-for-overview";
import { getDraftReturn } from "./schema/get-draft-return";
import { getEmailTemplateNames } from "./schema/get-email-template-names";
import { getFirstReturnForOrder } from "./schema/get-first-return-for-order";
import { getLabelRates } from "./schema/get-label-rates";
import { getManagedClaimStatus } from "./schema/get-managed-claim-status";
import { getOrder } from "./schema/get-order";
import { getOrderId } from "./schema/get-order-id";
import { getOrders } from "./schema/get-orders";
import { getOriginDiscounts } from "./schema/get-origin-discounts";
import { getRepairInvoiceDetails } from "./schema/get-repair-invoice-details";
import { getReturnTotals } from "./schema/get-return-totals";
import { getReturnTotalsFeatureFlag } from "./schema/get-return-totals-feature-flag";
import { getReturnsCount } from "./schema/get-returns-count";
import { getReturnsInstantExchangeRecoveryHistory } from "./schema/get-returns-instant-exchange-recovery-history";
import { getReturnsPagination } from "./schema/get-returns-pagination";
import { getSaveForLaterStorefrontDisplaySettings } from "./schema/get-save-for-later-storefront-display-settings";
import { getTnplContractStatus } from "./schema/get-tnpl-contract-status";
import { getUploadBundlesStatus } from "./schema/get-upload-bundles-status";
import { getVariantById } from "./schema/get-variant-by-id";
import { getWarehouseForProduct } from "./schema/get-warehouse-for-product";
import { getWarrantyRegistration } from "./schema/get-warranty-registration";
import { getWarrantyRegistrations } from "./schema/get-warranty-registrations";
import { getWarrantyRegistrationsCount } from "./schema/get-warranty-registrations-count";
import { getGiftReceiptRequests } from "./schema/gift-receipt-requests/get-gift-receipt-requests";
import { updateGiftReceiptRequestStatus } from "./schema/gift-receipt-requests/update-gift-receipt-request-status";
import { getGlobalSearchResults } from "./schema/global-search/get-global-search-results";
import { getCurrentImsForecastingInvoice } from "./schema/ims/get-current-ims-forecasting-invoice";
import { getCurrentImsInvoice } from "./schema/ims/get-current-ims-invoice";
import { getFinalizedImsForecastingInvoices } from "./schema/ims/get-finalized-ims-forecasting-invoices";
import { getFinalizedImsInvoices } from "./schema/ims/get-finalized-ims-invoices";
import { getIndustryCategoryComparisonStats } from "./schema/industry-category/get-industry-category-comparison-stats";
import { getValidIndustryCategories } from "./schema/industry-category/get-valid-industry-categories";
import { getInmarBlindReturns } from "./schema/inmar-blind-returns/get-inmar-blind-returns";
import { updateInmarBlindReturnStatus } from "./schema/inmar-blind-returns/update-inmar-blind-return-status";
import { authorizeFaireIntegration } from "./schema/integrations/authorize-faire-integration";
import { getBigcommerceIntegration } from "./schema/integrations/bigcommerce/get-bigcommerce-integration";
import { setBigcommerceExchangeOrderStatus } from "./schema/integrations/bigcommerce/set-bigcommerce-exchange-order-status";
import { setBigcommerceRefundMethod } from "./schema/integrations/bigcommerce/set-bigcommerce-refund-method";
import { disconnectCloudXIntegration } from "./schema/integrations/cloudx/disconnect-cloudx-integration";
import { getCloudXIntegration } from "./schema/integrations/cloudx/get-cloudx-integration";
import { postCloudXIntegration } from "./schema/integrations/cloudx/post-cloudx-integration";
import { createCommentsoldIntegration } from "./schema/integrations/commentsold/create-commentsold-integration";
import { deleteCommentsoldIntegration } from "./schema/integrations/commentsold/delete-commentsold-integration";
import { enableOmsOnCommentsoldIntegration } from "./schema/integrations/commentsold/enable-oms-on-commentsold-integration";
import { getCommentsoldIntegration } from "./schema/integrations/commentsold/get-commentsold-integration";
import { getCommentsoldOAuthUrl } from "./schema/integrations/commentsold/get-commentsold-oauth-url";
import { updateCommentsoldIntegration } from "./schema/integrations/commentsold/update-commentsold-integration";
import { createCin7CoreIntegration } from "./schema/integrations/create-cin7-core-integration";
import { createCin7OmniIntegration } from "./schema/integrations/create-cin7-omni-integration";
import { createOmsApparelMagicIntegration } from "./schema/integrations/create-oms-apparel-magic-integration";
import { createTiktokIntegration } from "./schema/integrations/create-tiktok-integration";
import { disconnectFaireIntegration } from "./schema/integrations/disconnect-faire-integration";
import { disconnectOmsTiktokIntegration } from "./schema/integrations/disconnect-oms-tiktok-integration";
import { disconnectWalmartIntegration } from "./schema/integrations/disconnect-walmart-integration";
import { doesEbayIntegrationExist } from "./schema/integrations/does-ebay-integration-exist";
import { exchangeTrackstarAuthCode } from "./schema/integrations/exchange-trackstar-auth-code";
import { getAmazonAuthUrl } from "./schema/integrations/get-amazon-auth-url";
import { getAmazonIntegration } from "./schema/integrations/get-amazon-integration";
import { getCin7CoreIntegration } from "./schema/integrations/get-cin7-core-integration";
import { getCin7OmniIntegration } from "./schema/integrations/get-cin7-omni-integration";
import { getEbayAuthorizationUrl } from "./schema/integrations/get-ebay-authorization-url";
import { getEnabledFulfillmentProviders } from "./schema/integrations/get-enabled-fulfillment-providers";
import { getFaireIntegration } from "./schema/integrations/get-faire-integration";
import { getLegacySignedOAuthState } from "./schema/integrations/get-legacy-signed-oauth-state";
import { getMarketingEcommerceProviders } from "./schema/integrations/get-marketing-ecommerce-providers";
import { getOmsApparelMagicIntegration } from "./schema/integrations/get-oms-apparel-magic-integration";
import { getOmsTiktokIntegration } from "./schema/integrations/get-oms-tiktok-integration";
import { getTeamEcommerceProviders } from "./schema/integrations/get-team-ecommerce-providers";
import { getTiktokAuthorizationExpiration } from "./schema/integrations/get-tiktok-authorization-expiration";
import { getTrackstarConnections } from "./schema/integrations/get-trackstar-connections";
import { getTrackstarLinkToken } from "./schema/integrations/get-trackstar-link-token";
import { getWalmartIntegration } from "./schema/integrations/get-walmart-integration";
import { disconnectGlobalEIntegration } from "./schema/integrations/globale/disconnect-globale-integration";
import { getGlobalEIntegration } from "./schema/integrations/globale/get-globale-integration";
import { setGlobalEIntegration } from "./schema/integrations/globale/set-globale-integration";
import { disconnectIDFShippingIntegration } from "./schema/integrations/idf-shipping/disconnect-idf-shipping-integration";
import { getIDFShippingIntegration } from "./schema/integrations/idf-shipping/get-idf-shipping-integration";
import { setIDFShippingIntegration } from "./schema/integrations/idf-shipping/set-idf-shipping-integration";
import { disconnectKatanaIntegration } from "./schema/integrations/katana/disconnect-katana-integration";
import { getKatanaIntegration } from "./schema/integrations/katana/get-katana-integration";
import { setKatanaIntegration } from "./schema/integrations/katana/set-katana-integration";
import { createLandmarkGlobalIntegration } from "./schema/integrations/landmark-global/create-landmark-global-integration";
import { getLandmarkGlobalIntegration } from "./schema/integrations/landmark-global/get-landmark-global-integration";
import { disconnectLuminousIntegration } from "./schema/integrations/luminous/disconnect-luminous-integration";
import { getLuminousIntegration } from "./schema/integrations/luminous/get-luminous-integration";
import { setLuminousIntegration } from "./schema/integrations/luminous/set-luminous-integration";
import { updateLuminousIntegration } from "./schema/integrations/luminous/update-luminous-integration";
import { deleteNetSuiteIntegration } from "./schema/integrations/netsuite/delete-netsuite-integration";
import { getNetSuiteIntegration } from "./schema/integrations/netsuite/get-netsuite-integration";
import { testNetSuiteReturnAuthorization } from "./schema/integrations/netsuite/test-netsuite-return-authorization";
import { upsertNetSuiteIntegration } from "./schema/integrations/netsuite/upsert-netsuite-integration";
import { createOmsNetsuiteIntegration } from "./schema/integrations/oms-netsuite/create-oms-netsuite-integration";
import { getOmsNetsuiteIntegration } from "./schema/integrations/oms-netsuite/get-oms-netsuite-integration";
import { updateOmsNetsuiteIntegration } from "./schema/integrations/oms-netsuite/update-oms-netsuite-integration";
import { createParentEasypostIntegration } from "./schema/integrations/parent-easypost/create-parent-easypost-integration";
import { deleteParentEasypostIntegration } from "./schema/integrations/parent-easypost/delete-parent-easypost-integration";
import { getParentEasypostIntegration } from "./schema/integrations/parent-easypost/get-parent-easypost-integration";
import { updateParentEasypostIntegration } from "./schema/integrations/parent-easypost/update-parent-easypost-integration";
import { disconnectRefundidIntegration } from "./schema/integrations/refundid/disconnect-refundid-integration";
import { getRefundidIntegration } from "./schema/integrations/refundid/get-refundid-integration";
import { setRefundidIntegration } from "./schema/integrations/refundid/set-refundid-integration";
import { resendExternalReturnShippingProviderLabel } from "./schema/integrations/resend-external-return-shipping-provider-label";
import { getReturnBearExceptions } from "./schema/integrations/returnbear/get-return-bear-exceptions";
import { getReturnBearTrackingEvents } from "./schema/integrations/returnbear/get-returnbear-tracking-events";
import { resolveReturnBearException } from "./schema/integrations/returnbear/resolve-return-bear-exception";
import { getShopifyIntegration } from "./schema/integrations/shopify/get-shopify-integration";
import { setShopifyRefundMethod } from "./schema/integrations/shopify/set-shopify-refund-method";
import { disconnectTeamworkCommerceIntegration } from "./schema/integrations/teamwork-commerce/disconnect-teamwork-commerce-integration";
import { getTeamworkCommerceIntegration } from "./schema/integrations/teamwork-commerce/get-teamwork-commerce-integration";
import { setTeamworkCommerceIntegration } from "./schema/integrations/teamwork-commerce/set-teamwork-commerce-integration";
import { updateCin7CoreIntegration } from "./schema/integrations/update-cin7-core-integration";
import { updateTrackstarReturnConnectionSettings } from "./schema/integrations/update-trackstar-return-connection-settings";
import { createWayfairIntegration } from "./schema/integrations/wayfair/create-wayfair-integration";
import { disconnectWayfairIntegration } from "./schema/integrations/wayfair/disconnect-wayfair-integration";
import { getWayfairIntegration } from "./schema/integrations/wayfair/get-wayfair-integration";
import { issueStoreCreditToCustomer } from "./schema/issue-store-credit-to-customer";
import { getMerchantRecommendedExchanges } from "./schema/merchant-recommended-exchanges/get-merchant-recommended-exchanges";
import { getProductsForExchangeGrouping } from "./schema/merchant-recommended-exchanges/get-products-for-exchange-grouping";
import { updateMerchantRecommendedExchanges } from "./schema/merchant-recommended-exchanges/update-merchant-recommended-exchanges";
import { getEditSourceCounts } from "./schema/order-edits/get-edit-source-counts";
import { getEditTypeCounts } from "./schema/order-edits/get-edit-type-counts";
import { getUpsellRevenue } from "./schema/order-edits/get-upsell-revenue";
import { searchOrderEdits } from "./schema/order-edits/search-order-edits";
import { searchOrderEditsCount } from "./schema/order-edits/search-order-edits-count";
import { detectCustomsInvoiceDiscrepancies } from "./schema/orders/detect-customs-invoice-discrepancies";
import { getOrdersWithPickStatus } from "./schema/orders/get-orders-with-pick-status";
import { getTeamOrdersByEmail } from "./schema/orders/get-team-orders-by-email";
import { syncOrder } from "./schema/orders/sync-order";
import { syncOrderByProviderId } from "./schema/orders/sync-order-by-provider-id";
import { addOutboundFunds } from "./schema/outbound-labels/add-outbound-funds";
import { cancelPickup } from "./schema/outbound-labels/cancel-pickup";
import { createPickup } from "./schema/outbound-labels/create-ups-pickup";
import { getPendingUpsPickups } from "./schema/outbound-labels/get-pending-ups-pickups";
import {
  searchPickups,
  searchPickupsCount,
} from "./schema/outbound-labels/search-pickups";
import { createPackingSlipTemplate } from "./schema/packing-slips/create-packing-slip-template";
import { deletePackingSlipTemplate } from "./schema/packing-slips/delete-packing-slip-template";
import { getPackingSlipTemplate } from "./schema/packing-slips/get-packing-slip-template";
import { getPackingSlipTemplatesByTeam } from "./schema/packing-slips/get-packing-slip-templates-by-team";
import { updatePackingSlipTemplate } from "./schema/packing-slips/update-packing-slip-template";
import { getPayoutInvoiceCsv } from "./schema/payouts/get-payout-invoice-csv";
import { getPayoutInvoicePdf } from "./schema/payouts/get-payout-invoice-pdf";
import { getPoEmailSettings } from "./schema/po-email-settings/get-po-email-settings";
import { sendTestPoEmail } from "./schema/po-email-settings/send-test-po-email";
import { updatePoEmailSettings } from "./schema/po-email-settings/update-po-email-settings";
import { processReturn } from "./schema/process-return";
import { classifyProducts } from "./schema/products/classify-products";
import { getProduct } from "./schema/products/get-product";
import { getProductVendors } from "./schema/products/get-product-vendors";
import { searchProductTags } from "./schema/products/search-product-tags";
import { searchProducts } from "./schema/products/search-products";
import { purchaseLabel } from "./schema/purchase-label";
import { getRateCalculatorRates } from "./schema/rate-calculator/get-rate-calculator-rates";
import { removeVariantExchangeItem } from "./schema/remove-variant-exchange-item";
import { resendEmail } from "./schema/resend-email";
import { resendGiftCard } from "./schema/resend-gift-card";
import { resetOrderFlow } from "./schema/reset-order-flow/reset-order-flow";
import { restockItemsForReturn } from "./schema/restock/restock-items-for-return";
import { setReturnItemDisposition } from "./schema/return-item-disposition/set-return-item-disposition";
import { getAiChats } from "./schema/return-page-details/get-ai-chats";
import { getAiReturnReasonsConversation } from "./schema/return-page-details/get-ai-return-reasons-conversation";
import { getProductVariantMetafields } from "./schema/return-page-details/get-product-variant-metafields";
import { getReturnItemBinLocation } from "./schema/return-page-details/get-return-item-bin";
import { getReturnOrderNotes } from "./schema/return-page-details/get-return-order-notes";
import { getRivoCustomerLoyalty } from "./schema/return-page-details/get-rivo-customer-loyalty";
import { provideMessageFeedback } from "./schema/return-page-details/provide-message-feedback";
import { refundExchangeShipping } from "./schema/return-page-details/refund-exchange-shipping";
import { submitReturnShippingClaim } from "./schema/return-page-details/submit-return-shipping-claim";
import { attemptReturnPickupRefund } from "./schema/return-pickup/attempt-return-pickup-refund";
import { rescheduleReturnPickup } from "./schema/return-pickup/reschedule-return-pickup";
import { scheduleReturnPickup } from "./schema/return-pickup/schedule-return-pickup";
import { createReturnViewFolder } from "./schema/return-views/create-return-view-folder";
import { deleteReturnView } from "./schema/return-views/delete-return-view";
import { deleteReturnViewFolder } from "./schema/return-views/delete-return-view-folder";
import { getReturnViewNotificationCounts } from "./schema/return-views/get-return-view-notification-counts";
import { getReturnViewsAndFolders } from "./schema/return-views/get-return-views-and-folders";
import { saveReturnView } from "./schema/return-views/save-return-view";
import { saveReturnViewsAndFolders } from "./schema/return-views/save-return-views-and-folders";
import { updateReturnViewFolderName } from "./schema/return-views/update-return-view-folder";
import { addTagToReturn } from "./schema/returns/add-tag-to-return";
import {
  getCurrentReturnAiExchangeAgentInvoice,
  getFinalizedReturnAiExchangeAgentInvoices,
} from "./schema/returns/ai-exchange-agent-invoices";
import { changePendingLockStatus } from "./schema/returns/change-pending-lock-status";
import { dismissCreateShipmentFailure } from "./schema/returns/dismiss-create-shipment-failure";
import { dismissRmaCreationFailure } from "./schema/returns/dismiss-rma-creation-failure";
import { enableAiExchangeAgent } from "./schema/returns/enable-ai-exchange-agent";
import { getCurrentReturnsInvoice } from "./schema/returns/get-current-returns-invoice";
import { getFinalizedReturnsInvoices } from "./schema/returns/get-finalized-returns-invoices";
import { removeTagFromReturn } from "./schema/returns/remove-tag-from-return";
import { updateProductReturn } from "./schema/returns/update-product-return";
import { updateReturnAdjustment } from "./schema/returns/update-return-adjustment";
import { updateReturnCustomerContact } from "./schema/returns/update-return-customer-contact";
import { updateReturnPermissions } from "./schema/returns/update-return-permissions";
import { getRedoSalesChannels } from "./schema/sales-channels/get-redo-sales-channels";
import { getSalesChannelOptions } from "./schema/sales-channels/get-sales-channel-options";
import { setRedoSalesChannels } from "./schema/sales-channels/set-redo-sales-channels";
import { searchProductVariants } from "./schema/search-product-variants";
import { sendRepairInvoice } from "./schema/send-repair-invoice";
import { getCartValidationFunctionStatus } from "./schema/shopify/get-cart-validation-function-status";
import { getShopifyPlan } from "./schema/shopify/get-shopify-plan";
import { getProductIdToProductAsVariantProductIds } from "./schema/shopify/products-as-variants/get-product-id-to-pav-product-ids";
import { getProductsAsVariantsConfigurations } from "./schema/shopify/products-as-variants/get-products-as-variants-configurations";
import { syncProductsAsVariants } from "./schema/shopify/products-as-variants/sync-products-as-variants";
import { testProductsAsVariantsConfigurations } from "./schema/shopify/products-as-variants/test-products-as-variants-configurations";
import { updateProductsAsVariantsConfigurations } from "./schema/shopify/products-as-variants/update-products-as-variants-configurations";
import { refreshShopifyPlan } from "./schema/shopify/refresh-shopify-plan";
import { toggleCartValidationFunction } from "./schema/shopify/toggle-cart-validation-function";
import { speechToText } from "./schema/speech-to-text";
import { createSpotlight } from "./schema/spotlights/create-spotlight";
import { createSpotlightSplit } from "./schema/spotlights/create-spotlight-split";
import { deleteSpotlight } from "./schema/spotlights/delete-spotlight";
import { duplicateSpotlight } from "./schema/spotlights/duplicate-spotlight";
import { getSpotlight } from "./schema/spotlights/get-spotlight";
import { getSpotlightSplits } from "./schema/spotlights/get-spotlight-splits";
import { getSpotlights } from "./schema/spotlights/get-spotlights";
import { updateSpotlight } from "./schema/spotlights/update-spotlight";
import { updateSpotlightSplitStatus } from "./schema/spotlights/update-spotlight-split-status";
import { createCallAvailability } from "./schema/support/create-call-availability";
import {
  getCsatResponsesForConversation,
  getCsatResponsesForTeam,
  getCsatResponsesForTeamCount,
} from "./schema/support/csat";
import { deleteRecordingFromCallRecord } from "./schema/support/delete-recording-from-call-record";
import { generatePdfForInvoice } from "./schema/support/generate-pdf-for-invoice";
import { getCallRecordWithTranscript } from "./schema/support/get-call-record-with-transcript";
import { getConversationInsights } from "./schema/support/get-conversation-insights";
import { getConversationInsightsCount } from "./schema/support/get-conversation-insights-count";
import { getConversationTagsById } from "./schema/support/get-conversation-tags-by-ids";
import { getSupportAiPricing } from "./schema/support/get-support-ai-pricing";
import { getSupportBillingInvoices } from "./schema/support/get-support-billing-invoices";
import { getTeamCallAvailabilities } from "./schema/support/get-team-call-availabilities";
import { grantFreeTrialCredits } from "./schema/support/grant-free-trial-credits";
import { hasReachedConversationThreshold } from "./schema/support/has-reached-conversation-threshold";
import { manuallyConvertChatToEmail } from "./schema/support/manually-convert-chat-to-email";
import { getPhoneNumberForTeam } from "./schema/support/phone-number";
import { polishMessage } from "./schema/support/polish-message";
import { searchTelnyxPhoneNumbers } from "./schema/support/search-telnyx-phone-numbers";
import { setupPhoneNumber } from "./schema/support/setup-phone-number";
import { getTelnyxTelephonyToken } from "./schema/support/telnyx-telephony-token";
import { toggleCallBeingRecorded } from "./schema/support/toggle-call-being-recorded";
import { toggleCallOnHold } from "./schema/support/toggle-call-on-hold";
import { turnOnSupportDefaults } from "./schema/support/turn-on-support-defaults";
import { updateAiFeedbackOnMessage } from "./schema/support/update-ai-feedback-on-message";
import { updateSupportSettings } from "./schema/support/update-support-settings";
import { createTagGroup } from "./schema/tag-groups/create-tag-group";
import { deleteTagGroup } from "./schema/tag-groups/delete-tag-group";
import { getTagGroups } from "./schema/tag-groups/get-tag-groups";
import { updateTagGroup } from "./schema/tag-groups/update-tag-group";
import { deleteTag } from "./schema/tags/delete-tag";
import { editTag } from "./schema/tags/edit-tag";
import { getTagsByTagGroup } from "./schema/tags/get-tags-by-tag-group";
import { getTeamTags } from "./schema/tags/get-team-tags";
import { upsertTag } from "./schema/tags/upsert-tag";
import { getPickedSettings } from "./schema/team-settings/get-picked-settings";
import { updatePickedSettings } from "./schema/team-settings/update-picked-settings";
import { watchTeamEvents } from "./schema/team/watch-team-events";
import { createThirdPartyBillingAccount } from "./schema/third-party-billing-accounts/create-third-party-billing-account";
import { deleteThirdPartyBillingAccount } from "./schema/third-party-billing-accounts/delete-third-party-billing-account";
import { getThirdPartyBillingAccounts } from "./schema/third-party-billing-accounts/get-third-party-billing-accounts";
import { checkShopifySelector } from "./schema/try-first/check-shopify-selector";
import { editTryFirstPlan } from "./schema/try-first/edit-try-first-plan";
import { getTryFirstPlan } from "./schema/try-first/get-try-first-plan";
import { updateTryFirstWidgetSettings } from "./schema/try-first/update-try-first-widget-settings";
import { updateNewOrderItems } from "./schema/update-new-order-items";
import { updatePosSettings } from "./schema/update-pos-settings";
import { updateRepairInvoice } from "./schema/update-repair-invoice";
import { updateReturnWithShippingRefund } from "./schema/update-return-with-shipping-refund";
import { updateReturnWithWaivedShippingFee } from "./schema/update-return-with-waived-shipping-fee";
import { updateSaveForLaterStorefrontDisplaySettings } from "./schema/update-save-for-later-storefront-display-settings";
import { uploadBundles } from "./schema/upload-bundles";
import { uploadSkuGroupingCsv } from "./schema/upload-sku-grouping-csv";
import { getUpsellPageAnalytics } from "./schema/upsell-page-analytics.ts/get-upsell-page-analytics";
import { getPostPurchaseAnalyticsByCheckoutToken } from "./schema/upsell-page-analytics/get-post-purchase-analytics-by-checkout-token";
import { getUpsellProductAnalytics } from "./schema/upsell-page-analytics/get-upsell-product-analytics";
import { createUpsellPage } from "./schema/upsell-page/create-upsell-page";
import { getActiveUpsellPageForTeam } from "./schema/upsell-page/get-active-upsell-page-for-team";
import { getUpsellPage } from "./schema/upsell-page/get-upsell-page";
import { updateUpsellPage } from "./schema/upsell-page/update-upsell-page";
import { getUserPin } from "./schema/users/get-user-pin";
import { getProductsForVariantExchangeGuidance } from "./schema/variant-exchange-guidance/get-products-for-variant-exchange-guidance";
import { getVariantExchangeGuidance } from "./schema/variant-exchange-guidance/get-variant-exchange-guidance";
import { updateVariantExchangeGuidance } from "./schema/variant-exchange-guidance/update-variant-exchange-guidance";
import { watchReturn } from "./schema/watch-return";
import { activateWebPixelSetting } from "./schema/web-pixel/activate-web-pixel-setting";
import { deactivateWebPixelSetting } from "./schema/web-pixel/deactivate-web-pixel-setting";
import { getCustomerYofiScores } from "./schema/yofi/get-yofi-scores";

const returnAssessmentDefinitions = {
  createAssessment,
  getAssessmentDefinition,
  getAssessmentList,
  getAssessmentGroupList,
  getReturnProductAssessments,
  updateAssessment,
  deleteAssessment,
  createGroup,
  updateGroup,
  deleteGroup,
  addAssessmentToReturn,
  removeAssessmentFromReturn,
  completeAssessmentOnReturn,
  updateProductAssessmentAssignee,
};

const integrationRpcDefinitions = {
  createCin7CoreIntegration,
  createCin7OmniIntegration,
  createTiktokIntegration,
  createOmsApparelMagicIntegration,
  doesEbayIntegrationExist,
  getCin7CoreIntegration,
  getCin7OmniIntegration,
  updateCin7CoreIntegration,
  getEbayAuthorizationUrl,
  getLegacySignedOAuthState,
  getOmsTiktokIntegration,
  disconnectOmsTiktokIntegration,
  getTiktokAuthorizationExpiration,
  getAmazonAuthUrl,
  getAmazonIntegration,
  getBlueYonderIntegration,
  setBlueYonderIntegration,
  disconnectBlueYonderIntegration,
  getFlexportIntegration,
  putFlexportIntegration,
  disconnectFlexportIntegration,
  getInmarIntegration,
  setInmarIntegration,
  disconnectInmarIntegration,
  disconnectWalmartIntegration,
  disconnectFaireIntegration,
  authorizeFaireIntegration,
  getTrackstarConnections,
  getTrackstarLinkToken,
  exchangeTrackstarAuthCode,
  updateTrackstarReturnConnectionSettings,
  getWalmartIntegration,
  getFaireIntegration,
  getOmsApparelMagicIntegration,
  getOmsNetsuiteIntegration,
  createOmsNetsuiteIntegration,
  updateOmsNetsuiteIntegration,
  getGlobalEIntegration,
  setGlobalEIntegration,
  disconnectGlobalEIntegration,
  createLandmarkGlobalIntegration,
  getLandmarkGlobalIntegration,
  getIDFShippingIntegration,
  setIDFShippingIntegration,
  disconnectIDFShippingIntegration,
  getKatanaIntegration,
  setKatanaIntegration,
  disconnectKatanaIntegration,
  getNetSuiteIntegration,
  deleteNetSuiteIntegration,
  upsertNetSuiteIntegration,
  testNetSuiteReturnAuthorization,
  getLuminousIntegration,
  setLuminousIntegration,
  updateLuminousIntegration,
  disconnectLuminousIntegration,
  getKlaviyoIntegration,
  setKlaviyoIntegration,
  updateKlaviyoIntegration,
  disconnectKlaviyoIntegration,
  getLoopIntegration,
  setLoopIntegration,
  updateLoopIntegration,
  disconnectLoopIntegration,
  getRefundidIntegration,
  setRefundidIntegration,
  disconnectRefundidIntegration,
  getApparelMagicIntegration,
  setApparelMagicIntegration,
  updateApparelMagicIntegration,
  disconnectApparelMagicIntegration,
  getAims360Integration,
  setAims360Integration,
  updateAims360Integration,
  disconnectAims360Integration,
  getAuthorizeNetIntegration,
  setAuthorizeNetIntegration,
  updateAuthorizeNetIntegration,
  disconnectAuthorizeNetIntegration,
  getCustomReturnIntegration,
  setCustomReturnIntegration,
  disconnectCustomReturnIntegration,
  getCloudXIntegration,
  postCloudXIntegration,
  disconnectCloudXIntegration,
  getReturnBearIntegration,
  setReturnBearIntegration,
  updateReturnBearIntegration,
  disconnectReturnBearIntegration,
  getReturnBearExceptions,
  getReturnBearTrackingEvents,
  resolveReturnBearException,
  getRiseAiIntegration,
  setRiseAiIntegration,
  updateRiseAiIntegration,
  disconnectRiseAiIntegration,
  getPurpleDotIntegration,
  setPurpleDotIntegration,
  updatePurpleDotIntegration,
  disconnectPurpleDotIntegration,
  getCreditsYardIntegration,
  setCreditsYardIntegration,
  updateCreditsYardIntegration,
  disconnectCreditsYardIntegration,
  getSekoIntegration,
  setSekoIntegration,
  updateSekoIntegration,
  disconnectSekoIntegration,
  getShipHeroIntegration,
  putShipheroIntegration,
  disconnectShipHeroIntegration,
  shipHeroInventoryAdd,
  validateShipHeroLocation,
  getShipedgeIntegration,
  putShipedgeIntegration,
  disconnectShipedgeIntegration,
  getCommentsoldIntegration,
  createCommentsoldIntegration,
  deleteCommentsoldIntegration,
  enableOmsOnCommentsoldIntegration,
  updateCommentsoldIntegration,
  getCommentsoldOAuthUrl,
  getTeamworkCommerceIntegration,
  setTeamworkCommerceIntegration,
  disconnectTeamworkCommerceIntegration,
  getParentEasypostIntegration,
  createParentEasypostIntegration,
  updateParentEasypostIntegration,
  deleteParentEasypostIntegration,
  getWayfairIntegration,
  createWayfairIntegration,
  disconnectWayfairIntegration,
};

const coreRpcDefinitions = {
  // brand kit
  generateContactCard,
  processFontFiles,
  updateBrandKit,
  // billing
  createStripeConnectedAccount,
  onboardStripeConnectedAccount,
  getStripeConnectedAccount,
  triggerManualPayout,
  // third-party billing accounts
  createThirdPartyBillingAccount,
  deleteThirdPartyBillingAccount,
  getThirdPartyBillingAccounts,
  // address autofill
  getAddressFromSuggestion,
  getAutocompleteSuggestions,
  // addresses
  createAddress,
  deleteAddress,
  getAddresses,
  getAddressCollections,
  getAddressesByIds,
  getOrCreateExternalAddress,
  updateAddress,
  // advanced flow
  batchUpdateRuleOrder,
  createAdvancedFlow,
  createAdvancedFlowFromDefault,
  deleteAdvancedFlow,
  getAdvancedFlowById,
  getAdvancedFlowCount,
  getAdvancedFlows,
  getAdvancedFlowsByVersionGroupId,
  getRulesByTrigger,
  updateAdvancedFlow,
  // email templates
  createEmailTemplateFromDefault,
  // product classification
  classifyProducts,
  // agent
  redoAi,
  provideAiFeedback,
  provideMessageFeedback,
  // ai summary
  generateAiAnalyticsSummary,
  getAiAnalyticsSummary,
  // faq
  createArticle,
  createCollection,
  createFaqPage,
  deleteArticle,
  deleteCollection,
  deleteFaqPage,
  getFaq,
  getFaqArticles,
  getFaqCollections,
  getFaqPages,
  getFaqTheme,
  updateArticle,
  updateCollection,
  updateFaqPage,
  updateTheme,
  // collection
  getCollectionInfoByIds,
  getCollections,
  getWorkflowStatus,
  createRmaForReturn,
  triggerEmailFlowPreview,
  sendEmailFromTemplate,
  getValidReturnTrackingTriggers,
  // managed claims
  getManagedClaimStatus,
  fastTrackClaim,
} as const;

const orderRpcDefinitions = {
  // order
  addLocation,
  deleteLocation,
  updateLocation,
  addOutboundFunds,
  cancelPickup,
  createPickup,
  searchPickups,
  searchPickupsCount,
  getPendingUpsPickups,
  authorizeUspsCarrierAccount,
  verifyUspsContractRatesLinked,
  createCarrierAccount,
  createDirectFedexCarrierAccount,
  deleteCarrierAccount,
  detectCustomsInvoiceDiscrepancies,
  syncOrder,
  syncOrderByProviderId,
  generateFedexPin,
  getCarrierAccounts,
  submitFedexMfaData,
  submitFedexMfaBackup,
  getLocations,
  getLocationsWithFulfillsFor,
  resetOrderFlow,
  getFirstReturnForOrder,
  getOrdersWithPickStatus,
  getTeamOrdersByEmail,
  checkOrdersReturnability,
  // order edits
  getEditSourceCounts,
  getEditTypeCounts,
  getUpsellRevenue,
  searchOrderEdits,
  searchOrderEditsCount,
  // packing
  createPackingSlipTemplate,
  deletePackingSlipTemplate,
  getPackingSlipTemplate,
  getPackingSlipTemplatesByTeam,
  updatePackingSlipTemplate,
  // product
  getProduct,
  getProductVendors,
  searchProducts,
  searchProductTags,
  // shopify
  getShopifyPlan,
  refreshShopifyPlan,
  getCartValidationFunctionStatus,
  toggleCartValidationFunction,
  // repair
  getRepairInvoiceDetails,
  sendRepairInvoice,
  updateRepairInvoice,
  // rate calculator
  getRateCalculatorRates,
} as const;

const productsAsVariantsRpcDefinitions = {
  syncProductsAsVariants,
  testProductsAsVariantsConfigurations,
  updateProductsAsVariantsConfigurations,
  getProductsAsVariantsConfigurations,
  getProductIdToProductAsVariantProductIds,
} as const;

const merchantRecommendedExchangesRpcDefinitions = {
  getProductsForExchangeGrouping,
  getMerchantRecommendedExchanges,
  updateMerchantRecommendedExchanges,
} as const;

const variantExchangeGuidanceRpcDefinitions = {
  getVariantExchangeGuidance,
  getProductsForVariantExchangeGuidance,
  updateVariantExchangeGuidance,
} as const;

const supportRpcDefinitions = {
  // return page details
  getAiChats,
  getAiReturnReasonsConversation,
  getProductVariantMetafields,
  getReturnItemBinLocation,
  getReturnOrderNotes,
  getRivoCustomerLoyalty,
  refundExchangeShipping,
  // returns
  submitReturnShippingClaim,
  // support
  createCallAvailability,
  deleteRecordingFromCallRecord,
  getCallRecordWithTranscript,
  generatePdfForInvoice,
  getCsatResponsesForConversation,
  getCsatResponsesForTeam,
  getCsatResponsesForTeamCount,
  getPhoneNumberForTeam,
  getSupportAiPricing,
  getSupportBillingInvoices,
  getTeamCallAvailabilities,
  getTelnyxTelephonyToken,
  hasReachedConversationThreshold,
  manuallyConvertChatToEmail,
  polishMessage,
  searchTelnyxPhoneNumbers,
  setupPhoneNumber,
  toggleCallBeingRecorded,
  toggleCallOnHold,
  turnOnSupportDefaults,
  updateAiFeedbackOnMessage,
  updateSupportSettings,
  getConversationTagsById,
  getConversationInsights,
  getConversationInsightsCount,
  grantFreeTrialCredits,
  // tag
  deleteTag,
  editTag,
  getTagsByTagGroup,
  getTeamTags,
  upsertTag,
  // tag groups
  createTagGroup,
  deleteTagGroup,
  getTagGroups,
  updateTagGroup,
  // teams
  watchTeamEvents,
  // team settings
  getPickedSettings,
  updatePickedSettings,
} as const;

const miscRpcDefinitions = {
  // cart optimization
  editCartOptimizationSettings,
  // try first
  checkShopifySelector,
  editTryFirstPlan,
  getTryFirstPlan,
  updateTryFirstWidgetSettings,
  getTnplContractStatus,
  // upsell
  createUpsellPage,
  updateUpsellPage,
  getUpsellPage,
  getActiveUpsellPageForTeam,
  // warranty
  getWarrantyEligibleProducts,
  getWarrantyRegistration,
  getWarrantyRegistrations,
  getWarrantyRegistrationsCount,
  submitWarrantyRegistration,
  // yofi
  getCustomerYofiScores,
  // gift receipt requests
  getGiftReceiptRequests,
  updateGiftReceiptRequestStatus,
  // inmar blind returns
  getInmarBlindReturns,
  updateInmarBlindReturnStatus,
  // other
  calculateNewOrderValue,
  // global search
  getGlobalSearchResults,
  generateFlowAgentResponse,
  createCustomerAuthToken,
  createOrderComment,
  createReport,
  getCustomer,
  getCustomerByEmail,
  getCustomerTags,
  deleteReport,
  disconnectRivoIntegration,
  getUserPin,
  adjustLoyaltyPoints,
  getAiExchangeAgentAnalytics,
  getPortalEvents,
  getPortalOptimizationState,
  getBundlesData,
  getClaimAttachmentRateSeriesData,
  getClaimsAnalyticsStatistics,
  getCustomerAccountsMetricsForOverview,
  getDraftReturn,
  getEmailTemplateNames,
  getExchangeRates,
  getIndustryCategoryComparisonStats,
  getLabelRates,
  getNativeAccountsSettings,
  getOperationsNewTotals,
  getOrCreateCustomerFromPhoneNumber,
  getOrderId,
  getOrder,
  getOrders,
  cancelOrder,
  getBatchTnplContractStatus,
  getOriginDiscounts,
  getProductBenchmark,
  getProductTitles,
  getRedoSalesChannels,
  getReport,
  getReports,
  getReturnAnalyticsByProduct,
  getReturnAnalyticsByVariant,
  getReturnAnalyticsSeriesData,
  getReturnAnalyticsStatistics,
  getReturnOperationsKeyStats,
  getTryFirstAnalytics,
  streamProductsReportData,
  streamOrdersReportData,
  streamWarrantyCustomersReportData,
  streamPurchasedExtendedWarrantyReportData,
  getReturnsInstantExchangeRecoveryHistory,
  getRivoIntegration,
  getSalesChannelOptions,
  getSaveForLaterStorefrontDisplaySettings,
  getTags,
  getUpsellPageAnalytics,
  getPostPurchaseAnalyticsByCheckoutToken,
  getUpsellProductAnalytics,
  getValidIndustryCategories,
  getVendorNames,
  getOrderSourceNames,
  getWarehouseForProduct,
  issueStoreCreditToCustomer,
  processReturn,
  attemptReturnPickupRefund,
  rescheduleReturnPickup,
  scheduleReturnPickup,
  purchaseLabel,
  removeVariantExchangeItem,
  resendExternalReturnShippingProviderLabel,
  resendEmail,
  resendGiftCard,
  restockItemsForReturn,
  searchProductVariants,
  getVariantById,
  setRedoSalesChannels,
  setReturnItemDisposition,
  setRivoIntegration,
  updateCustomerWidgetSettings,
  updateAllCustomerWidgetSettings,
  updateNativeAccountsSettings,
  // web pixel
  activateWebPixelSetting,
  deactivateWebPixelSetting,
  updateNewOrderItems,
  updateReport,
  findPreviousReturnShippingRefunds,
  updateReturnWithShippingRefund,
  updateReturnWithWaivedShippingFee,
  updateSaveForLaterStorefrontDisplaySettings,
  getReturnsCount,
  getReturnsPagination,
  getReturnTotals,
  getReturnTotalsFeatureFlag,
  watchReturn,
  uploadBundles,
  uploadSkuGroupingCsv,
  getUploadBundlesStatus,
  // return views
  createReturnViewFolder,
  deleteReturnView,
  deleteReturnViewFolder,
  getReturnViewNotificationCounts,
  getReturnViewsAndFolders,
  saveReturnView,
  saveReturnViewsAndFolders,
  updateReturnViewFolderName,
  updatePosSettings,
  updateReturnPermissions,
  addTagToReturn,
  removeTagFromReturn,
  dismissCreateShipmentFailure,
  dismissRmaCreationFailure,
  enableAiExchangeAgent,
  getCurrentReturnAiExchangeAgentInvoice,
  getFinalizedReturnAiExchangeAgentInvoices,
  getCurrentReturnsInvoice,
  getFinalizedReturnsInvoices,
  updateProductReturn,
  updateReturnAdjustment,
  updateReturnCustomerContact,
  changePendingLockStatus,
  // speech-to-text
  speechToText,
  // payout invoices
  getPayoutInvoiceCsv,
  getPayoutInvoicePdf,
} as const;
const merchantRpcDefinitions = {
  messageCustomerPortalBuilder,
  saveCustomerPortalBuilderChanges,
  messageMerchantSupportAgent,
  streamMerchantSupportAgent,
  getMerchantSupportAgentThreads,
  getMerchantSupportAgentThread,
  queryDom,
  createEscalation,
  getEscalation,
  getEscalationMessages,
  sendEscalationMessage,
  watchEscalationEvents,
} as const;

const ecommerceRpcDefinitions = {
  // bigcommerce
  getBigcommerceIntegration,
  setBigcommerceExchangeOrderStatus,
  setBigcommerceRefundMethod,
  // shopify
  getShopifyIntegration,
  setShopifyRefundMethod,
  // integrations
  getEnabledProviders: getEnabledFulfillmentProviders,
  getTeamEcommerceProviders,
  getMarketingEcommerceProviders,
} as const;

const aeoRpcDefinitions = {
  addAeoPrompts,
  archiveAeoPrompts,
  createCompetitorOverride,
  deleteCompetitorOverride,
  generateAeoPrompts,
  getAeoPromptDetails,
  getAeoPromptResponses,
  getAeoPrompts,
  getAeoPromptsFiltered,
  getBrandIdentityGuide,
  getBrandVisibilityOverTime,
  getCompetitorComparison,
  getCompetitorOverrides,
  getCurrentAeoInvoice,
  getDashboardMetrics,
  getFinalizedAeoInvoices,
  getModelPerformance,
  getPromptsByCategory,
  getSources,
  getSourcesByDomain,
  unarchiveAeoPrompts,
} as const;

const imsRpcDefinitions = {
  getCurrentImsInvoice,
  getFinalizedImsInvoices,
  getCurrentImsForecastingInvoice,
  getFinalizedImsForecastingInvoices,
  getPoEmailSettings,
  sendTestPoEmail,
  updatePoEmailSettings,
} as const;

const spotlightRpcDefinitions = {
  getSpotlights,
  getSpotlight,
  createSpotlight,
  updateSpotlight,
  deleteSpotlight,
  duplicateSpotlight,
  getSpotlightSplits,
  createSpotlightSplit,
  updateSpotlightSplitStatus,
} as const;

const aiToneAndVoiceRpcDefinitions = {
  initializeAiToneAndVoice,
  getAiToneAndVoiceSettings,
  updateAiToneAndVoiceSettings,
} as const;

const disputeRpcDefinitions = {
  getDispute,
  getDisputes,
  getShopPolicies,
  submitDisputeEvidence,
  updateDisputeEvidence,
  updateShopPolicy,
  generateDisputeActivityLog,
  generateCustomerCommunicationEvidence,
} as const;

const freightRpcDefinitions = {
  generateFreightQuotes,
  purchaseFreightBooking,
} as const;

export const rpcDefinition: typeof coreRpcDefinitions &
  typeof orderRpcDefinitions &
  typeof supportRpcDefinitions &
  typeof miscRpcDefinitions &
  typeof ecommerceRpcDefinitions &
  typeof returnAssessmentDefinitions &
  typeof merchantRpcDefinitions &
  typeof productsAsVariantsRpcDefinitions &
  typeof merchantRecommendedExchangesRpcDefinitions &
  typeof variantExchangeGuidanceRpcDefinitions &
  typeof integrationRpcDefinitions &
  typeof aeoRpcDefinitions &
  typeof imsRpcDefinitions &
  typeof spotlightRpcDefinitions &
  typeof aiToneAndVoiceRpcDefinitions &
  typeof disputeRpcDefinitions &
  typeof freightRpcDefinitions = {
  ...coreRpcDefinitions,
  ...orderRpcDefinitions,
  ...supportRpcDefinitions,
  ...miscRpcDefinitions,
  ...ecommerceRpcDefinitions,
  ...merchantRpcDefinitions,
  ...returnAssessmentDefinitions,
  ...integrationRpcDefinitions,
  ...productsAsVariantsRpcDefinitions,
  ...merchantRecommendedExchangesRpcDefinitions,
  ...variantExchangeGuidanceRpcDefinitions,
  ...aeoRpcDefinitions,
  ...imsRpcDefinitions,
  ...spotlightRpcDefinitions,
  ...aiToneAndVoiceRpcDefinitions,
  ...disputeRpcDefinitions,
  ...freightRpcDefinitions,
};

// Performance optimization: Pre-compute group types separately instead of one giant
// InferRpcDefinition<typeof rpcDefinition>. This allows TypeScript to cache intermediate
// type computations rather than re-inferring ~300+ endpoints in a single pass.
// Reduces emit time from ~10.4s to ~2.3s (4.5x faster).
// See: https://github.com/microsoft/TypeScript/wiki/Performance#preferring-interfaces-over-intersections
type CoreRpc = InferRpcDefinition<typeof coreRpcDefinitions>;
type OrderRpc = InferRpcDefinition<typeof orderRpcDefinitions>;
type SupportRpc = InferRpcDefinition<typeof supportRpcDefinitions>;
type MiscRpc = InferRpcDefinition<typeof miscRpcDefinitions>;
type EcommerceRpc = InferRpcDefinition<typeof ecommerceRpcDefinitions>;
type ReturnAssessmentRpc = InferRpcDefinition<
  typeof returnAssessmentDefinitions
>;
type MerchantRpc = InferRpcDefinition<typeof merchantRpcDefinitions>;
type ProductsAsVariantsRpc = InferRpcDefinition<
  typeof productsAsVariantsRpcDefinitions
>;
type MerchantRecommendedExchangesRpc = InferRpcDefinition<
  typeof merchantRecommendedExchangesRpcDefinitions
>;
type VariantExchangeGuidanceRpc = InferRpcDefinition<
  typeof variantExchangeGuidanceRpcDefinitions
>;
type IntegrationRpc = InferRpcDefinition<typeof integrationRpcDefinitions>;
type AeoRpc = InferRpcDefinition<typeof aeoRpcDefinitions>;
type ImsRpc = InferRpcDefinition<typeof imsRpcDefinitions>;
type SpotlightRpc = InferRpcDefinition<typeof spotlightRpcDefinitions>;
type AiToneAndVoiceRpc = InferRpcDefinition<
  typeof aiToneAndVoiceRpcDefinitions
>;
type DisputeRpc = InferRpcDefinition<typeof disputeRpcDefinitions>;
type FreightRpc = InferRpcDefinition<typeof freightRpcDefinitions>;

export type RedoMerchantRpcDefinition = CoreRpc &
  OrderRpc &
  SupportRpc &
  MiscRpc &
  EcommerceRpc &
  ReturnAssessmentRpc &
  MerchantRpc &
  ProductsAsVariantsRpc &
  MerchantRecommendedExchangesRpc &
  VariantExchangeGuidanceRpc &
  IntegrationRpc &
  AeoRpc &
  ImsRpc &
  SpotlightRpc &
  AiToneAndVoiceRpc &
  DisputeRpc &
  FreightRpc;
